import { createTheme, alpha } from '@mui/material/styles';
import { landingHeroLayoutTokens } from './landingHeroLayout';
/**
 * West Virginia–inspired theme: state blue, gold accent, professional and modern.
 * Use for palette, typography, transitions, and section gradients.
 */
/** WV brand — primary blue + accent gold. Only these two hexes; no separate light/dark shades. */
export const WV_BLUE = '#002957';
const wvBlue = WV_BLUE;
/** Single source for all gold UI (buttons, accents) — #CC9F38 */
export const WV_GOLD = '#CFA053';
const wvGold = WV_GOLD;
/**
 * Global typography stacks — use MUI `Typography` + `variant` so these apply automatically.
 * - `variant="h1"` … `variant="h6"` → Arial
 * - `variant="body1"` | `body2` | `subtitle1` | `subtitle2` → Arial
 * - `variant="caption"` | `overline` → Arial; use for meta, chips, footer fine print, and uppercase eyebrows
 * - `Button` → Arial via `typography.button`
 * Avoid repeating `fontFamily` in `sx` unless you are not using a Typography/Button or need a one-off override.
 */
export const FONT_BODY = 'Arial, Helvetica, sans-serif';
export const FONT_HEADING = FONT_BODY;
const fontBody = FONT_BODY;
const fontHeading = FONT_HEADING;
export const appTheme = createTheme({
    palette: {
        mode: 'light',
        primary: {
            main: wvBlue,
            contrastText: '#ffffff',
        },
        secondary: {
            main: wvGold,
            /** White on gold is 2.45:1 (fails WCAG AA) — navy on gold is 6.52:1. */
            contrastText: "#ffffff",
        },
        error: {
            main: '#c62828',
            light: '#ef5350',
            dark: '#b71c1c',
            contrastText: '#ffffff',
        },
        /** Permit tags / info surfaces — harmonize with primary blue. */
        info: {
            main: '#1565c0',
            light: '#42a5f5',
            dark: '#0d47a1',
            contrastText: '#ffffff',
        },
        /** Warnings / highlights — distinct from secondary gold. */
        warning: {
            main: '#e65100',
            light: '#ff9800',
            dark: '#bf360c',
            contrastText: '#ffffff',
        },
        success: {
            main: '#2e7d32',
            light: '#4caf50',
            dark: '#1b5e20',
            contrastText: '#ffffff',
        },
        background: {
            default: '#f0f4f8',
            paper: '#ffffff',
        },
        text: {
            primary: wvBlue,
            secondary: '#4a5568',
            disabled: '#9e9e9e',
        },
        grey: {
            50: '#f8fafc',
            100: '#f0f4f8',
            200: '#e2e8f0',
            300: '#cbd5e1',
            400: '#94a3b8',
            500: '#64748b',
            600: '#475569',
            700: '#334155',
            800: '#1e293b',
            900: '#0f172a',
        },
    },
    typography: {
        fontFamily: fontBody,
        h1: {
            fontFamily: fontHeading,
            fontWeight: 500,
            lineHeight: 1.12,
            letterSpacing: '0.02em',
            /** Breakpoint `rem` only — avoids `vmin`/`vw` inversions vs Windows display scaling (see `landingHeroLayout`). */
            /* MUI `TypographyStyleOptions.fontSize` is typed as `string | number` only; breakpoint maps are valid at runtime. */
            fontSize: {
                xs: '1.6rem',
                sm: '1.8rem',
                md: '2.1rem',
                lg: '2.45rem',
                xl: '2.75rem',
            },
        },
        h2: { fontFamily: fontHeading, fontWeight: 500, fontSize: '1.875rem', letterSpacing: '0.02em' },
        h3: {
            fontFamily: fontHeading,
            fontWeight: 500,
            lineHeight: 1.2,
            fontSize: {
                xs: '1.35rem',
                sm: '1.5rem',
                md: '1.75rem',
                lg: '1.95rem',
                xl: '2.125rem',
            },
        },
        h4: { fontFamily: fontHeading, fontWeight: 500, fontSize: '1.375rem' },
        h5: { fontFamily: fontHeading, fontWeight: 500, fontSize: '1.2rem' },
        h6: { fontFamily: fontHeading, fontWeight: 500, fontSize: '1.0625rem' },
        subtitle1: { fontFamily: fontBody, fontWeight: 500, fontSize: '1rem' },
        subtitle2: { fontFamily: fontBody, fontWeight: 600, fontSize: '0.875rem' },
        body1: { fontFamily: fontBody, fontSize: '1rem', lineHeight: 1.6, fontWeight: 400 },
        body2: { fontFamily: fontBody, fontSize: '0.875rem', lineHeight: 1.55, fontWeight: 400 },
        caption: {
            fontFamily: fontBody,
            fontSize: '0.75rem',
            lineHeight: 1.4,
            fontWeight: 400,
        },
        overline: {
            fontFamily: fontBody,
            fontSize: '0.75rem',
            lineHeight: 1.25,
            fontWeight: 600,
            letterSpacing: '0.04em',
            textTransform: 'uppercase',
        },
        button: { fontWeight: 600, textTransform: 'none', fontFamily: fontBody, fontSize: '0.9375rem' },
    },
    shape: { borderRadius: 10 },
    /** Landing hero: widths, breakpoints, `svh` rhythm — see `landingHeroLayout.ts` + `theme.augment.d.ts`. */
    landingHeroLayout: landingHeroLayoutTokens,
    /** Same apparent 8px grid at default 16px root, but scales when `html` uses fluid font-size. */
    spacing: (factor) => `${0.5 * factor}rem`,
    transitions: {
        duration: { shortest: 150, shorter: 200, short: 250, standard: 300, complex: 375, enteringScreen: 225, leavingScreen: 195 },
        easing: { easeInOut: 'cubic-bezier(0.4, 0, 0.2, 1)', easeOut: 'cubic-bezier(0, 0, 0.2, 1)', easeIn: 'cubic-bezier(0.4, 0, 1, 1)' },
    },
    components: {
        MuiButton: {
            styleOverrides: {
                root: {
                    borderRadius: 10,
                    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                    '&:hover': { transform: 'translateY(-1px)', boxShadow: '0 4px 12px rgba(12,38,70,0.18)' },
                },
                containedSecondary: {
                    backgroundColor: wvGold,
                    color: wvBlue,
                    '&:hover': {
                        backgroundColor: wvGold,
                        color: wvBlue,
                    },
                    '&:disabled': {
                        backgroundColor: alpha(wvGold, 0.4),
                        color: alpha(wvBlue, 0.8),
                    },
                },
                outlinedSecondary: {
                    borderColor: wvGold,
                    color: wvGold,
                    '&:hover': {
                        borderColor: wvGold,
                        backgroundColor: alpha(wvGold, 0.08),
                        color: wvGold,
                    },
                },
                textSecondary: {
                    color: wvGold,
                    '&:hover': {
                        backgroundColor: alpha(wvGold, 0.08),
                        color: wvGold,
                    },
                },
            },
            defaultProps: { disableElevation: true },
        },
        MuiPaper: {
            styleOverrides: {
                root: {
                    borderRadius: 12,
                    transition: 'box-shadow 0.3s ease, transform 0.2s ease',
                },
            },
        },
        MuiCard: {
            styleOverrides: {
                root: {
                    borderRadius: 12,
                    transition: 'box-shadow 0.3s ease, transform 0.2s ease, border-color 0.2s ease',
                },
            },
        },
    },
});
/**
 * Section gradients and backgrounds (WV-inspired).
 */
export const sectionBackgrounds = {
    hero: `linear-gradient(135deg, ${wvBlue} 0%, ${alpha(wvBlue, 0.85)} 50%, ${alpha(wvBlue, 0.8)} 100%)`,
    heroOverlay: alpha(wvBlue, 0.92),
    light: `linear-gradient(180deg, #ffffff 0%, ${alpha(wvBlue, 0.03)} 100%)`,
    subtle: `linear-gradient(180deg, ${appTheme.palette.grey[50]} 0%, ${appTheme.palette.grey[100]} 100%)`,
    accent: `linear-gradient(135deg, ${alpha(wvGold, 0.08)} 0%, ${alpha(wvBlue, 0.04)} 100%)`,
    /** Same warm band as CtaBlock default — pair with FAQ `sectionBackground` for a unified look */
    ctaWarmRadial: 'radial-gradient(ellipse 85% 65% at 50% 35%, #fffbf0 0%, #fff8e8 28%, #ffffff 72%, #ffffff 100%)',
};
/** Resolve gradient prop (string or { from, to, direction }) to CSS background value */
export function resolveGradient(gradient, fallback) {
    if (!gradient)
        return fallback;
    if (typeof gradient === 'string')
        return gradient;
    const { from, to, direction = '135deg' } = gradient;
    if (from && to)
        return `linear-gradient(${direction}, ${from} 0%, ${to} 100%)`;
    return fallback;
}
export default appTheme;
